import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { GameLeaderboardResponse } from '@lunit/shared-types';
import { gameApi } from '@core/game/api';
import { useUser } from '@core/contexts/ProfileContext';
import { useVenue } from '@core/contexts/VenueContext';
import { PodiumCard } from '@core/game/components/PodiumCard';
import { LeaderboardItem } from '@core/game/components/LeaderboardItem';

type Period = 'daily' | 'weekly' | 'alltime';

export const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { venue } = useVenue();
  const [period, setPeriod] = useState<Period>('weekly');

  const venueId = venue?.id ?? 'demo';

  const { data, isLoading, error } = useQuery<GameLeaderboardResponse>({
    queryKey: ['leaderboard', period, venueId],
    queryFn: () => gameApi.getLeaderboard(period, 50, venueId),
    refetchInterval: 30000
  });

  const entries = data?.entries ?? [];

  const [top3, rest] = useMemo(() => {
    const podium = entries.slice(0, 3);
    const others = entries.slice(3);
    return [podium, others] as const;
  }, [entries]);

  const currentUserEntry = useMemo(
    () => entries.find((entry) => entry.userId === user?.id),
    [entries, user?.id]
  );

  const currentUserRank = currentUserEntry?.rank ?? null;

  const hasData = entries.length > 0;

  return (
    <div className="min-h-screen bg-gradient-dark text-white">
      <header className="sticky top-0 z-10 bg-primary/80 backdrop-blur-lg border-b border-white/10 p-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/game')}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <Trophy className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">Leaderboard</h1>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          {(['daily', 'weekly', 'alltime'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`flex-1 py-2 px-4 rounded-2xl font-semibold transition-all ${
                period === p ? 'bg-white text-primary shadow-lg' : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {p === 'daily' && 'Today'}
              {p === 'weekly' && 'This Week'}
              {p === 'alltime' && 'All Time'}
            </button>
          ))}
        </div>
      </header>

      <main className="p-4 pb-36">
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-white" />
          </div>
        )}

        {!isLoading && error && (
          <div className="rounded-lg border border-red-500 bg-red-500/20 p-4 text-center text-sm">
            {error instanceof Error ? error.message : 'Failed to load leaderboard.'}
          </div>
        )}

        {!isLoading && !error && !hasData && (
          <div className="flex flex-col items-center justify-center py-16 text-center text-gray-300">
            <Trophy className="mb-4 h-16 w-16 text-gray-500" />
            <p>No players yet. Be the first to claim the top spot!</p>
          </div>
        )}

        {!isLoading && !error && hasData && (
          <>
            {top3.length > 0 && (
              <section className="mb-10">
                <h2 className="mb-4 text-center text-xl font-bold">Top Players</h2>
                <div className="flex items-end justify-center gap-4">
                  {top3[1] && <PodiumCard entry={top3[1]} rank={2} />}
                  {top3[0] && <PodiumCard entry={top3[0]} rank={1} />}
                  {top3[2] && <PodiumCard entry={top3[2]} rank={3} />}
                </div>
              </section>
            )}

            {rest.length > 0 && (
              <section className="space-y-2">
                <h2 className="text-lg font-semibold">All Players</h2>
                {rest.map((entry) => (
                  <LeaderboardItem
                    key={`${entry.userId}-${entry.rank}`}
                    entry={entry}
                    isCurrentUser={entry.userId === user?.id}
                  />
                ))}
              </section>
            )}
          </>
        )}
      </main>

      {currentUserEntry && currentUserRank && currentUserRank > 3 && (
        <footer className="fixed bottom-0 left-0 right-0 z-20 border-t border-white/10 bg-primary/95 backdrop-blur-lg p-4 shadow-2xl">
          <div className="mb-2 text-center text-xs text-text-muted uppercase tracking-wide">
            Your Position
          </div>
          <LeaderboardItem entry={currentUserEntry} isCurrentUser />
        </footer>
      )}
    </div>
  );
};

